import { ModerationSettings } from '@tribeplatform/gql-client/types'
import { useAdminOnboarding } from '@tribeplatform/react-components/AdminOnboarding'
import { Form } from '@tribeplatform/react-components/Form'
import { T, useI18n } from '@tribeplatform/react-components/i18n'
import { useUpdateModerationSettings } from '@tribeplatform/react-sdk/hooks'
import { Button } from '@tribeplatform/react-ui-kit/Button'
import { Card } from '@tribeplatform/react-ui-kit/Card'
import { LoadingOverlay } from '@tribeplatform/react-ui-kit/LoadingOverlay'
import { toast } from '@tribeplatform/react-ui-kit/Toast'

interface AgedBasedModerationProps {
  moderationSettings: ModerationSettings
}

const ageOptions = [
  {
    text: 'Disabled',
    value: 0,
  },
  {
    text: '1 hour',
    value: 1,
  },
  {
    text: '1 day',
    value: 24,
  },
  {
    text: '3 days',
    value: 3 * 24,
  },
  {
    text: '7 days',
    value: 7 * 24,
  },
  {
    text: '30 days',
    value: 30 * 24,
  },
  {
    text: 'Forever',
    value: 999999999,
  },
]

export const AgedBasedModeration = ({
  moderationSettings,
}: AgedBasedModerationProps) => {
  const { $t } = useI18n()
  const { mutateAsync: updateModerationSettings, isLoading } =
    useUpdateModerationSettings()
  const { completeStep } = useAdminOnboarding()

  const { member } = moderationSettings
  const { minimumHoursToPost, minimumHoursToReply } = member || {}
  const enable = minimumHoursToPost > 0 || minimumHoursToReply > 0

  const onSubmit = data => {
    const { minimumHoursToPost, minimumHoursToReply, enable } = data

    updateModerationSettings({
      input: {
        member: {
          minimumHoursToPost: enable ? parseInt(minimumHoursToPost, 10) : 0,
          minimumHoursToReply: enable ? parseInt(minimumHoursToReply, 10) : 0,
        },
      },
    })
      .then(() => {
        completeStep({
          step: 'moderation-rules',
        })
        toast({
          title: $t({
            defaultMessage: 'Moderation settings updated.',
            id: 'Admin.ModerationSettings.Updated',
          }),
          status: 'success',
        })
      })
      .catch(() => {
        toast({
          title: $t({
            defaultMessage: 'Unable to update moderation settings.',
            id: 'Admin.ModerationSettings.FailedToUpdate',
          }),
          status: 'error',
        })
      })
  }

  return (
    <Form
      defaultValues={{
        enable,
        minimumHoursToPost,
        minimumHoursToReply,
      }}
      onSubmit={onSubmit}
    >
      {({ formState: { isDirty }, watch }) => (
        <Card>
          <Card.Header
            title={$t({
              defaultMessage: 'Account age-based moderation',
              id: 'Admin.ModerationSettings.AccountAgeBasedModeration',
            })}
            description={$t({
              defaultMessage:
                'Set criteria based on account age to determine which posts and replies are automatically held for moderation. This helps monitor content from newer accounts.',
              id: 'Admin.ModerationSettings.AccountAgeBasedModeration.Description',
            })}
          />
          <Card.Content>
            <div className="web:col-span-12 web:md:col-span-8 flex flex-col space-y-5">
              <Form.Toggle
                name="enable"
                label={$t({
                  defaultMessage: 'Enable account age-based moderation',
                  id: 'Admin.ModerationSettings.EnableAccountAgeBasedModeration',
                })}
              />
              <LoadingOverlay isLoading={!watch('enable')} pulse={false}>
                <div className="flex flex-col md:flex-row gap-5">
                  <div className="flex-1">
                    <Form.Select
                      className="flex-1"
                      name="minimumHoursToPost"
                      label={$t({
                        defaultMessage: 'New posts',
                        id: 'Admin.ModerationSettings.MinAge.ToPost',
                      })}
                      helperText={$t({
                        defaultMessage:
                          'Send all posts to moderation for accounts younger than this age.',
                        id: 'Admin.ModerationSettings.MinAge.ToPost.HelperText',
                      })}
                      items={ageOptions}
                    />
                  </div>
                  <div className="flex-1">
                    <Form.Select
                      className="flex-1"
                      name="minimumHoursToReply"
                      label={$t({
                        defaultMessage: 'New replies',
                        id: 'Admin.ModerationSettings.MinAge.ToReply',
                      })}
                      helperText={$t({
                        defaultMessage:
                          'Send all replies to moderation for accounts younger than this age.',
                        id: 'Admin.ModerationSettings.MinAge.ToReply.HelperText',
                      })}
                      items={ageOptions}
                    />
                  </div>
                </div>
              </LoadingOverlay>
            </div>
          </Card.Content>
          <Card.Actions>
            <Button type="submit" loading={isLoading} disabled={!isDirty}>
              <T defaultMessage="Update" id="Generics.Update" />
            </Button>
          </Card.Actions>
        </Card>
      )}
    </Form>
  )
}
